"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Field, Input, Textarea, Select, Checkbox, FormStatus, SubmitButton } from "@/components/ui/Form";
import { confirmDialog } from "@/lib/notifications/confirmDialog";
import { toast } from "@/lib/notifications/toast";
import { FILETYPE_IMAGE, validateFiles } from "@/lib/utils/fileValidation";
import {
  fetchUserAdvert,
  fetchAdvertOptions,
  createUserAdvert,
  updateUserAdvert,
  fetchUserAdvertImages,
  uploadUserAdvertImages,
  deleteUserAdvertImage,
  setUserAdvertMainImage,
  suggestAdvertCategory,
  type AdvertOption,
  type AdvertFormPayload,
  type UserAdvertImage,
} from "@/lib/api/c-advertisements-user";
import styles from "./CAdvertisements.module.css";

const schema = z
  .object({
    name: z.string().min(1, "Podaj nazwę ogłoszenia.").max(500, "Nazwa zbyt długa (max 500 znaków)."),
    text: z.string().min(1, "Podaj treść ogłoszenia."),
    category_id: z.string().min(1, "Wybierz kategorię.").refine((v) => Number(v) > 0, "Wybierz kategorię."),
    type_id: z.string().min(1, "Wybierz typ ogłoszenia.").refine((v) => Number(v) > 0, "Wybierz typ ogłoszenia."),
    availability: z.string().min(1, "Wybierz datę ważności."),
    negotiated_price: z.boolean(),
    price: z.string(),
    public: z.boolean(),
  })
  .refine(
    (v) => {
      if (v.negotiated_price) return true;
      const n = Number(v.price.replace(",", "."));
      return Number.isFinite(n) && n > 0;
    },
    { path: ["price"], message: "Podaj cenę większą od zera lub zaznacz 'do uzgodnienia'." },
  );

type FormValues = z.infer<typeof schema>;

/** Y-m-d dla input type=date (z plusem dni od dziś). */
function defaultAvailability(days = 30): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Formularz dodania / edycji ogłoszenia usera. `advertId` podany -> tryb edycji
 * (fetch advert + opcji + zdjęcia on mount). Bez `advertId` -> nowe ogłoszenie
 * (bez sekcji zdjęć - potrzebne id z backendu).
 *
 * **Zdjęcia inline** (tylko edit): upload multipart, siatka miniatur z akcjami
 * `usuń` / `ustaw jako okładkę` (is_main). Zapisywane niezależnie od form
 * fields - każda akcja to osobny call, nie wymaga "Zapisz zmiany" na formularzu.
 */
export function AdvertForm({
  advertId,
  onSaved,
  onCancel,
}: {
  advertId?: number;
  onSaved: (id: number) => void;
  onCancel: () => void;
}) {
  const isEdit = typeof advertId === "number";
  const [loading, setLoading] = useState(true);
  const [topError, setTopError] = useState<string | null>(null);
  const [categories, setCategories] = useState<AdvertOption[]>([]);
  const [types, setTypes] = useState<AdvertOption[]>([]);

  /* Zdjęcia - stan i akcje inline (tylko edit; w create sekcja ukryta). */
  const [images, setImages] = useState<UserAdvertImage[]>([]);
  const [uploading, setUploading] = useState(false);
  const [imgError, setImgError] = useState<string | null>(null);
  const [imgInfo, setImgInfo] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  /* Propozycja nowej kategorii - panel rozwijany pod polem `Kategoria`.
     Nie zapisuje encji w bazie; backend wysyla powiadomienie systemowe do
     grupy `system_administrator` (bez maila). */
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestName, setSuggestName] = useState("");
  const [suggestDesc, setSuggestDesc] = useState("");
  const [suggestSubmitting, setSuggestSubmitting] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: "onTouched",
    defaultValues: {
      name: "",
      text: "",
      category_id: "",
      type_id: "",
      availability: defaultAvailability(30),
      negotiated_price: false,
      price: "",
      public: true,
    },
  });

  const negotiated = watch("negotiated_price");
  const selectedCategoryId = watch("category_id");

  /* Typy zaweza sie do tych ktore naleza do parenta wybranej kategorii.
     Backend zwraca pelna liste typow (wszystkie c_advertisements w domenie) +
     `parent_id` per typ - tu filtrujemy client-side bez dodatkowego round-tripa. */
  const filteredTypes = useMemo(() => {
    const catId = Number(selectedCategoryId);
    if (!catId) return [] as typeof types;
    const cat = categories.find((c) => c.id === catId);
    if (!cat) return [] as typeof types;
    return types.filter((t) => t.parent_id === cat.parent_id);
  }, [selectedCategoryId, categories, types]);

  /* Reset type_id gdy zmiana kategorii sprawia, ze poprzednio wybrany typ nie
     nalezy juz do nowego parenta. Inaczej hidden stale id_type lecialoby na
     backend i odpadlo na walidacji. */
  useEffect(() => {
    const currentTypeId = Number(getValues("type_id"));
    if (!currentTypeId) return;
    if (!filteredTypes.some((t) => t.id === currentTypeId)) {
      setValue("type_id", "", { shouldValidate: false });
    }
  }, [filteredTypes, getValues, setValue]);

  const reloadImages = useCallback(async () => {
    if (!isEdit) return;
    try {
      const { images: rows } = await fetchUserAdvertImages(advertId!);
      setImages(rows);
    } catch (e) {
      setImgError(e instanceof Error ? e.message : String(e));
    }
  }, [isEdit, advertId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setTopError(null);
    (async () => {
      try {
        const opts = await fetchAdvertOptions();
        if (cancelled) return;
        setCategories(opts.categories);
        setTypes(opts.types);

        if (isEdit) {
          const [{ advert }, { images: imgs }] = await Promise.all([
            fetchUserAdvert(advertId!),
            fetchUserAdvertImages(advertId!),
          ]);
          if (cancelled) return;
          reset({
            name: advert.name,
            text: advert.text,
            category_id: String(advert.category_id),
            type_id: String(advert.type_id),
            availability: advert.availability ?? defaultAvailability(30),
            negotiated_price: advert.negotiated_price,
            price: advert.price !== null ? String(advert.price) : "",
            public: advert.public === 1,
          });
          setImages(imgs);
        }
      } catch (e) {
        if (!cancelled) setTopError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, advertId, reset]);

  const onSubmit = async (values: FormValues) => {
    setTopError(null);
    const payload: AdvertFormPayload = {
      name: values.name,
      text: values.text,
      category_id: Number(values.category_id),
      type_id: Number(values.type_id),
      availability: values.availability,
      negotiated_price: values.negotiated_price,
      price: values.negotiated_price ? null : Number(values.price.replace(",", ".")),
      public: values.public,
    };
    try {
      if (isEdit) {
        await updateUserAdvert(advertId!, payload);
        onSaved(advertId!);
      } else {
        const { advert_id } = await createUserAdvert(payload);
        onSaved(advert_id);
      }
    } catch (e) {
      setTopError(e instanceof Error ? e.message : String(e));
    }
  };

  const handleUpload = async (files: FileList | null) => {
    if (!isEdit || !files || files.length === 0) return;
    setImgError(null);
    setImgInfo(null);

    const { accepted, rejected } = validateFiles(files, FILETYPE_IMAGE);
    if (accepted.length === 0) {
      setImgError(rejected.map((r) => r.reason).join("\n"));
      if (fileRef.current) fileRef.current.value = "";
      return;
    }

    setUploading(true);
    try {
      const { uploaded, errors: uErrors } = await uploadUserAdvertImages(advertId!, accepted);
      const allErrors = [...rejected.map((r) => r.reason), ...uErrors];
      if (allErrors.length > 0) setImgError(allErrors.join("\n"));
      if (uploaded > 0) setImgInfo(`Wgrano zdjęć: ${uploaded}.`);
      await reloadImages();
    } catch (e) {
      setImgError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDeleteImage = async (img: UserAdvertImage) => {
    if (!isEdit) return;
    const ok = await confirmDialog({
      title: "Usunąć zdjęcie?",
      message: "Zdjęcie zostanie trwale usunięte z ogłoszenia.",
      confirmLabel: "Usuń",
      cancelLabel: "Anuluj",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await deleteUserAdvertImage(advertId!, img.id);
      await reloadImages();
    } catch (e) {
      setImgError(e instanceof Error ? e.message : String(e));
    }
  };

  const handleSetMainImage = async (img: UserAdvertImage) => {
    if (!isEdit) return;
    try {
      await setUserAdvertMainImage(advertId!, img.id);
      await reloadImages();
    } catch (e) {
      setImgError(e instanceof Error ? e.message : String(e));
    }
  };

  const handleSuggestSubmit = async () => {
    const name = suggestName.trim();
    if (name === "") {
      setSuggestError("Podaj nazwę proponowanej kategorii.");
      return;
    }
    setSuggestError(null);
    setSuggestSubmitting(true);
    try {
      await suggestAdvertCategory({ name, description: suggestDesc.trim() || undefined });
      toast.success("Dziękujemy! Twoja propozycja została przekazana do administratora.");
      setSuggestName("");
      setSuggestDesc("");
      setSuggestOpen(false);
    } catch (e) {
      setSuggestError(e instanceof Error ? e.message : String(e));
    } finally {
      setSuggestSubmitting(false);
    }
  };

  const handleSuggestCancel = () => {
    setSuggestOpen(false);
    setSuggestError(null);
    setSuggestName("");
    setSuggestDesc("");
  };

  if (loading) {
    return <p className={styles.empty}>Wczytywanie...</p>;
  }

  if (categories.length === 0 || types.length === 0) {
    return (
      <FormStatus variant="error">
        Brak skonfigurowanych kategorii lub typów ogłoszeń. Skontaktuj się z administratorem.
      </FormStatus>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      <h2 className={styles.formTitle}>{isEdit ? "Edytuj ogłoszenie" : "Nowe ogłoszenie"}</h2>

      <div className={styles.formRow}>
        <Field label="Kategoria" required error={errors.category_id?.message}>
          <Select disabled={isSubmitting} {...register("category_id")} placeholder="Wybierz...">
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Typ ogłoszenia" required error={errors.type_id?.message}>
          <Select
            disabled={isSubmitting || filteredTypes.length === 0}
            {...register("type_id")}
            placeholder={selectedCategoryId ? "Wybierz..." : "Najpierw wybierz kategorię"}
          >
            {filteredTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <div className={styles.suggestRow}>
        {!suggestOpen ? (
          <button
            type="button"
            className={styles.btnLink}
            onClick={() => setSuggestOpen(true)}
            disabled={isSubmitting}
          >
            Nie widzisz swojej kategorii? Zaproponuj nową
          </button>
        ) : (
          <div className={styles.suggestPanel}>
            <h4 className={styles.suggestTitle}>Zaproponuj nową kategorię</h4>
            <p className={styles.suggestHint}>
              Wyślemy Twoją propozycję do administratora. Nie zapiszemy jej jako kategorii - decyzja
              należy do administratora serwisu.
            </p>

            <Field label="Nazwa proponowanej kategorii" required error={suggestError ?? undefined}>
              <Input
                type="text"
                maxLength={200}
                value={suggestName}
                onChange={(e) => setSuggestName(e.target.value)}
                disabled={suggestSubmitting}
                placeholder="np. Sprzęt rowerowy - akcesoria"
              />
            </Field>

            <Field label="Uzasadnienie (opcjonalne)">
              <Textarea
                rows={3}
                maxLength={2000}
                value={suggestDesc}
                onChange={(e) => setSuggestDesc(e.target.value)}
                disabled={suggestSubmitting}
                placeholder="Dlaczego ta kategoria byłaby przydatna?"
              />
            </Field>

            <div className={styles.suggestActions}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={handleSuggestCancel}
                disabled={suggestSubmitting}
              >
                Anuluj
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleSuggestSubmit}
                disabled={suggestSubmitting}
              >
                {suggestSubmitting ? "Wysyłanie..." : "Wyślij propozycję"}
              </button>
            </div>
          </div>
        )}
      </div>

      <Field label="Nazwa" required error={errors.name?.message}>
        <Input type="text" maxLength={500} disabled={isSubmitting} {...register("name")} />
      </Field>

      <Field label="Treść" required error={errors.text?.message}>
        <Textarea rows={8} disabled={isSubmitting} {...register("text")} />
      </Field>

      <div className={styles.formRow}>
        <Field label="Ważne do" required error={errors.availability?.message}>
          <Input type="date" disabled={isSubmitting} {...register("availability")} />
        </Field>

        <Field label="Cena (PLN)" required={!negotiated} error={errors.price?.message}>
          <Input
            type="text"
            inputMode="decimal"
            disabled={isSubmitting || negotiated}
            placeholder={negotiated ? "do uzgodnienia" : "0.00"}
            {...register("price")}
          />
        </Field>
      </div>

      <Controller
        name="negotiated_price"
        control={control}
        render={({ field }) => (
          <Checkbox
            checked={!!field.value}
            onChange={(e) => field.onChange(e.target.checked)}
            disabled={isSubmitting}
          >
            Cena do uzgodnienia
          </Checkbox>
        )}
      />

      <Controller
        name="public"
        control={control}
        render={({ field }) => (
          <Checkbox
            checked={!!field.value}
            onChange={(e) => field.onChange(e.target.checked)}
            disabled={isSubmitting}
          >
            Ogłoszenie publikowane (widoczne na stronie)
          </Checkbox>
        )}
      />

      {isEdit && (
        <section className={styles.imagesSection}>
          <div className={styles.imagesToolbar}>
            <h3 className={styles.formTitle}>Zdjęcia</h3>
            <label className={styles.btnPrimary}>
              {uploading ? "Wgrywanie..." : "+ Dodaj zdjęcia"}
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                multiple
                hidden
                disabled={uploading}
                onChange={(e) => handleUpload(e.target.files)}
              />
            </label>
          </div>

          {imgError && <FormStatus variant="error">{imgError}</FormStatus>}
          {imgInfo && <FormStatus variant="success">{imgInfo}</FormStatus>}

          {images.length === 0 ? (
            <p className={styles.empty}>To ogłoszenie nie ma jeszcze zdjęć. Dodaj pierwsze!</p>
          ) : (
            <ul className={styles.imageGrid}>
              {images.map((img) => (
                <li key={img.id} className={img.is_main ? styles.imageItemMain : styles.imageItem}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={img.thumb_src} alt={img.description || ""} loading="lazy" decoding="async" />
                  {img.is_main && <span className={styles.mainBadge}>Okładka</span>}
                  <div className={styles.imageActions}>
                    {!img.is_main && (
                      <button type="button" className={styles.btnLink} onClick={() => handleSetMainImage(img)}>
                        Ustaw jako okładkę
                      </button>
                    )}
                    <button type="button" className={styles.btnDanger} onClick={() => handleDeleteImage(img)}>
                      Usuń
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {topError && <FormStatus variant="error">{topError}</FormStatus>}

      <div className={styles.formActions}>
        <button type="button" className={styles.btnSecondary} onClick={onCancel} disabled={isSubmitting}>
          {isEdit ? "Wróć do listy" : "Anuluj"}
        </button>
        <SubmitButton loading={isSubmitting}>{isEdit ? "Zapisz zmiany" : "Dodaj ogłoszenie"}</SubmitButton>
      </div>
    </form>
  );
}
